import React from "react";
import styled, { keyframes } from "styled-components";
import FileSaver from "file-saver";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Avatar } from "@mui/material";
import { DownloadRounded } from "@mui/icons-material";

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  background: ${({ theme }) => theme.card};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => `${theme.secondary}10`};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-8px);
    border-color: ${({ theme }) => theme.secondary};
    box-shadow: 0 20px 60px rgba(151, 71, 255, 0.3),
                0 0 0 1px ${({ theme }) => `${theme.secondary}40`};
  }
  
  &:nth-child(7n + 1) {
    grid-column: auto/span 2;
    grid-row: auto/span 2;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => `${theme.secondary}20`},
      transparent
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
    animation: ${shimmer} 3s infinite;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: start;
  gap: 8px;
  justify-content: end;
  flex-direction: column;
  backdrop-filter: blur(8px) saturate(180%);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  border-radius: 20px;
  opacity: 0;
  padding: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  color: ${({ theme }) => theme.white};

  ${Card}:hover & {
    opacity: 1;
  }
`;

const Prompt = styled.div`
  font-weight: 400;
  font-size: 15px;
  line-height: 1.4;
  color: ${({ theme }) => theme.white};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const Author = styled.div`
  font-weight: 600;
  font-size: 14px;
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${({ theme }) => theme.white};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const DownloadButton = styled(DownloadRounded)`
  padding: 8px;
  background: ${({ theme }) => theme.secondary};
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.primary};
    transform: scale(1.1);
  }
`;

const ImageCard = ({ item, heights }) => {
  return (
    <Card>
      <LazyLoadImage
        alt={item?.prompt}
        width="100%"
        src={item?.photo}
        style={{ borderRadius: "20px", objectFit: "cover" }}
      />
      <HoverOverlay>
        <Prompt>• {item?.prompt}</Prompt>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Author>
            <Avatar 
              sx={{ 
                background: "linear-gradient(135deg, #9747FF, #007AFF)", 
                width: "36px", 
                height: "36px",
                fontSize: "16px",
                fontWeight: "600"
              }}
            >
              {item?.name[0]}
            </Avatar>
            {item?.name}
          </Author>
          <DownloadButton
            onClick={(e) => {
              e.stopPropagation();
              FileSaver.saveAs(item?.photo, `${item?.prompt?.substring(0, 30) || 'download'}.jpg`);
            }}
          />
        </div>
      </HoverOverlay>
    </Card>
  );
};

export default ImageCard;
